import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

import PrivacyPolicy from "../other/PrivacyPolicy";
import PrivacyPolicyDe from "../other/PrivacyPolicyDe";
import PrivacyPolicySr from "../other/PrivacyPolicySr";
import { register } from "../../utils/Auth";
import DropdownSingleChoice from "../components/shared/DropdownSingleChoice";
import { setSnackbar } from "../../actions/snackbarActions";
import { clearAuthStorage } from "../../utils/tokenUtils";
import en from "../../locales/en/translation.json";
import sr from "../../locales/sr/translation.json";

import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import { styled, useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import {
  getPasswordValidationErrors,
  isPasswordFormatValid,
} from "../../utils/passwordValidation";

const validEmail1 = new RegExp(
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const BootstrapDialogTitle = (props) => {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

BootstrapDialogTitle.propTypes = {
  children: PropTypes.node,
  onClose: PropTypes.func.isRequired,
};

function Registration() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();

  const language = useSelector((state) => state.i18n.language);

  const [newUser, setNewUser] = useState({
    individual: true,
    companyAlreadyRegistered: false,
    companyName: "",
    tin: "",
    streetNumber: "",
    postcode: "",
    city: "",
    country: "",
    title: "",
    academicTitle: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    jobTitle: "",
    jobField: "",
    email: "",
    password: "",
  });

  const [confirmPW, setConfirmPW] = useState("");
  const [emailErr, setEmailErr] = useState(false);
  const [pwdError, setPwdError] = useState(false);
  const [confirmPwdError, setConfirmPwdError] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState([]);
  const [confirmPasswordErrors, setConfirmPasswordErrors] = useState([]);

  const [open, setOpen] = useState(false);
  const [helperTextString, setHelperTextString] = useState("");

  const [companyRegisteredAsBusiness, setCompanyRegisteredAsBusiness] =
    useState(false);
  const [agreeToPrivacyPolicy, setAgreeToPrivacyPolicy] = useState(false);
  const [titleValues, setTitleValues] = useState([]);
  const [academicTitleValues, setAcademicTitleValues] = useState([]);
  const [countryValues, setCountryValues] = useState([]);
  const [jobTitleValues, setJobTitleValues] = useState([]);
  const [jobFieldValues, setJobFieldValues] = useState([]);

  let Policy;

  switch (localStorage.getItem("i18nextLng")) {
    case "en":
      Policy = <PrivacyPolicy />;
      break;
    case "de":
      Policy = <PrivacyPolicyDe />;
      break;
    case "sr":
      Policy = <PrivacyPolicySr />;
      break;
    default:
      Policy = <PrivacyPolicy />;
  }

  const onSubmit = (e) => {
    e.preventDefault();

    const pwdErrors = getPasswordValidationErrors(newUser.password, t);
    const confirmErrors = [];

    if (pwdErrors.length) {
      setPwdError(true);
      setPasswordErrors(pwdErrors);
    }

    if (!confirmPW || newUser.password !== confirmPW) {
      confirmErrors.push(t("errorHelperText.passwordMatch"));
    } else {
      const confirmFormatErrors = getPasswordValidationErrors(confirmPW, t);
      confirmErrors.push(...confirmFormatErrors);
    }

    if (confirmErrors.length) {
      setConfirmPwdError(true);
      setConfirmPasswordErrors(confirmErrors);
    }

    if (!areMandatoryFieldsFilled(newUser) || pwdErrors.length || confirmErrors.length) {
      if (!areMandatoryFieldsFilled(newUser) && !pwdErrors.length && !confirmErrors.length) {
        dispatch(setSnackbar(true, "info", t("snackbar.mandatoryFields")));
      }
      return;
    }

    const requestData = {
      ...newUser,
      language: localStorage.getItem("i18nextLng"),
    };
    register(requestData)
      .then((response) => {
        if (response.statusCode >= 200 && response.statusCode < 300) {
          // Ensure user is not logged in until email is confirmed
          clearAuthStorage();
          window.scrollTo(0, 0);
          navigate("/", {
            replace: true,
            state: { registrationSuccess: true },
          });
        } else {
          dispatch(
            setSnackbar(
              true,
              "error",
              response?.message || t("snackbar.registerError"),
            ),
          );
        }
      })
      .catch((err) => {
        const message =
          err?.response?.data?.message ||
          err?.message ||
          t("snackbar.registerError");
        dispatch(setSnackbar(true, "error", message));
      });
  };

  const areMandatoryFieldsFilled = (user) => {
    if (!agreeToPrivacyPolicy) return false;
    if (!confirmPW || user.password !== confirmPW) return false;
    if (!isPasswordFormatValid(user.password)) return false;

    if (
      !user.firstName?.trim() ||
      !user.lastName?.trim() ||
      !user.phoneNumber?.trim() ||
      !user.email?.trim() ||
      !user.password
    ) {
      return false;
    }

    if (!user.individual) {
      if (!user.companyName?.trim() || !user.tin?.toString().trim()) {
        return false;
      }
    }

    return true;
  };

  const canSubmit = areMandatoryFieldsFilled(newUser);

  const validatePasswordValue = (password) => {
    if (!password) {
      setPwdError(false);
      setPasswordErrors([]);
      return [];
    }
    const errors = getPasswordValidationErrors(password, t);
    setPwdError(errors.length > 0);
    setPasswordErrors(errors);
    return errors;
  };

  const validateConfirmPasswordValue = (password, confirmPassword) => {
    if (!confirmPassword) {
      setConfirmPwdError(false);
      setConfirmPasswordErrors([]);
      return [];
    }

    const errors = [
      ...getPasswordValidationErrors(confirmPassword, t),
    ];
    if (password !== confirmPassword) {
      errors.push(t("errorHelperText.passwordMatch"));
    }

    const unique = [...new Set(errors)];
    setConfirmPwdError(unique.length > 0);
    setConfirmPasswordErrors(unique);
    return unique;
  };

  const handleChange = (e) => {
    let { name, value } = e.target;

    if (e.target.type === "checkbox") {
      value = e.target.checked;
    }

    if (e.target.type === "switch") {
      value = e.target.checked;
    }

    setNewUser({
      ...newUser,
      [name]: value,
    });

    if (name === "password") {
      validatePasswordValue(value);
      if (confirmPW) {
        validateConfirmPasswordValue(value, confirmPW);
      }
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const value = e.target.value;
    setConfirmPW(value);
    validateConfirmPasswordValue(newUser.password, value);
  };

  useEffect(() => {
    translateDropdownButtonValues(language);
  }, [language]);

  // Prevodjenje ponudjenih vrednosti
  const translateDropdownButtonValues = (langCode) => {
    let file;
    switch (langCode) {
      case "en":
        file = en;
        break;
      case "sr":
        file = sr;
        break;
      default:
        return;
    }

    let country = file.registration.country;
    let countryArray = Object.keys(country);
    setCountryValues(countryArray);

    let titles = file.registration.titles;
    let titlesArray = Object.keys(titles);
    setTitleValues(titlesArray);

    let academicTitles = file.registration.academicTitles;
    let academicTitlesArray = Object.keys(academicTitles);
    setAcademicTitleValues(academicTitlesArray);

    let jobTitles = file.registration.job_titles;
    let jobTitlesArray = Object.keys(jobTitles);
    setJobTitleValues(jobTitlesArray);

    let jobFields = file.registration.job_fields;
    let jobFieldsArray = Object.keys(jobFields);
    setJobFieldValues(jobFieldsArray);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const [values, setValues] = useState({
    showPassword: false,
    confirmPassword: false,
  });

  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleClickShowConfirmePassword = () => {
    setValues({
      ...values,
      confirmPassword: !values.confirmPassword,
    });
  };

  const handleMouseDownConfirmePassword = (event) => {
    event.preventDefault();
  };

  const validate_Email = (e) => {
    if (!validEmail1.test(e.target.value)) {
      setHelperTextString(t("errorHelperText.email"));
      setEmailErr(true);
    } else {
      setEmailErr(false);
      setHelperTextString("");
    }
  };

  return (
    <Container component="main" maxWidth="md" sx={{ mb: 4 }}>
      <Paper
        variant="outlined"
        sx={{ my: { xs: 3, md: 6 }, p: { xs: 2, md: 3 } }}
      >
        <Box
          component="form"
          method="POST"
          autoComplete="off"
          sx={{
            mt: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "stretch",
            width: "100%",
            "& .MuiTextField-root": { m: 0 },
            "& .MuiFormControl-root": { m: 0 },
          }}
        >
          <Typography
            component="h1"
            variant="h4"
            align="center"
            sx={{ color: theme.palette.primary.main }}
          >
            {t("registration.create_account")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: theme.palette.primary.main, fontSize: 16 }}
            >
              {t("registration.legalEntity")}
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={newUser.individual}
                  onChange={handleChange}
                  name="individual"
                  sx={{ color: theme.palette.primary.main }}
                />
              }
              label={t("registration.individual")}
              sx={{ m: 0, ml: 1 }}
            />
          </Box>

          {newUser.individual ? (
            <>
              <Typography
                component="h2"
                variant="h5"
                align="center"
                sx={{ color: theme.palette.primary.main }}
              >
                {t("registration.individual_information")}
              </Typography>
              <TextField
                required={false}
                fullWidth
                id="streetNumber"
                label={t("registration.street_number")}
                name="streetNumber"
                autoFocus
                value={newUser.streetNumber}
                onChange={handleChange}
                type="text"
              />
              <TextField
                required={false}
                fullWidth
                id="postcode"
                label={t("registration.post_code")}
                name="postcode"
                autoComplete={t("registration.post_code")}
                value={newUser.postcode}
                onChange={handleChange}
                type="number"
              />
              <TextField
                required={false}
                fullWidth
                id="city"
                label={t("registration.city")}
                name="city"
                autoComplete={t("registration.city")}
                value={newUser.city}
                onChange={handleChange}
                type="text"
              />
              <DropdownSingleChoice
                orientation="row"
                labelText={t("registration.countries")}
                translationValue="registration.country."
                name="country"
                values={countryValues}
                defaultValue={""}
                hideNone
                onChange={handleChange}
              />
            </>
          ) : (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    name="companyAlreadyRegistered"
                    checked={newUser.companyAlreadyRegistered}
                    onChange={handleChange}
                  />
                }
                label={t("registration.already_registered_company")}
                sx={{
                  m: 0,
                  color: theme.palette.primary.main,
                  alignItems: "center",
                }}
              />
              <Typography
                component="h2"
                variant="h5"
                align="center"
                sx={{ color: theme.palette.primary.main }}
              >
                {t("registration.company_information")}
              </Typography>
              {newUser.companyAlreadyRegistered ? (
                <>
                  <TextField
                    required
                    fullWidth
                    id="companyName"
                    label={t("registration.company_name")}
                    name="companyName"
                    autoComplete={t("registration.company_name")}
                    value={newUser.companyName}
                    onChange={handleChange}
                    type="text"
                  />
                  <TextField
                    required
                    fullWidth
                    id="tin"
                    label={t("registration.tin")}
                    name="tin"
                    autoComplete={t("registration.tin")}
                    value={newUser.tin}
                    onChange={handleChange}
                    type="number"
                  />
                </>
              ) : (
                <>
                  <TextField
                    required
                    fullWidth
                    id="companyName"
                    label={t("registration.company_name")}
                    name="companyName"
                    autoComplete={t("registration.company_name")}
                    value={newUser.companyName}
                    onChange={handleChange}
                    type="text"
                  />
                  <TextField
                    required
                    fullWidth
                    id="tin"
                    label={t("registration.tin")}
                    name="tin"
                    autoComplete={t("registration.tin")}
                    value={newUser.tin}
                    onChange={handleChange}
                    type="number"
                  />
                  <TextField
                    required={false}
                    fullWidth
                    id="streetNumber"
                    label={t("registration.street_number")}
                    name="streetNumber"
                    value={newUser.streetNumber}
                    onChange={handleChange}
                    type="text"
                  />
                  <TextField
                    required={false}
                    fullWidth
                    id="postcode"
                    label={t("registration.post_code")}
                    name="postcode"
                    autoComplete={t("registration.post_code")}
                    value={newUser.postcode}
                    onChange={handleChange}
                    type="number"
                  />
                  <TextField
                    required={false}
                    fullWidth
                    id="city"
                    label={t("registration.city")}
                    name="city"
                    autoComplete={t("registration.city")}
                    value={newUser.city}
                    onChange={handleChange}
                    type="text"
                  />
                  <DropdownSingleChoice
                    fullWidth
                    orientation="row"
                    labelText={t("registration.countries")}
                    translationValue="registration.country."
                    name="country"
                    values={countryValues}
                    defaultValue={""}
                    onChange={handleChange}
                  />
                </>
              )}
            </>
          )}

          <Typography
            component="h2"
            variant="h5"
            align="center"
            sx={{ color: theme.palette.primary.main }}
          >
            {t("registration.contact_details")}
          </Typography>
          <DropdownSingleChoice
            orientation="row"
            labelText={t("registration.title")}
            translationValue="registration.titles."
            name="title"
            values={titleValues}
            defaultValue={""}
            hideNone
            onChange={handleChange}
          />
          <DropdownSingleChoice
            labelText={t("registration.academicTitle")}
            translationValue="registration.academicTitles."
            name="academicTitle"
            values={academicTitleValues}
            defaultValue={""}
            onChange={handleChange}
          />
          <TextField
            required
            fullWidth
            id="firstName"
            label={t("registration.first_name")}
            name="firstName"
            autoComplete={t("registration.first_name")}
            value={newUser.firstName}
            onChange={handleChange}
            type="text"
          />
          <TextField
            required
            fullWidth
            id="lastName"
            label={t("registration.last_name")}
            name="lastName"
            autoComplete={t("registration.last_name")}
            value={newUser.lastName}
            onChange={handleChange}
            type="text"
          />
          <TextField
            required
            fullWidth
            id="phoneNumber"
            label={t("registration.phone_number")}
            name="phoneNumber"
            autoComplete={t("registration.phone_number")}
            value={newUser.phoneNumber}
            onChange={handleChange}
            type="tel"
          />
          <DropdownSingleChoice
            orientation="row"
            labelText={t("registration.job_title")}
            translationValue="registration.job_titles."
            name="jobTitle"
            values={jobTitleValues}
            defaultValue={""}
            onChange={handleChange}
          />
          <DropdownSingleChoice
            orientation="row"
            labelText={t("registration.job_field")}
            translationValue="registration.job_fields."
            name="jobField"
            values={jobFieldValues}
            defaultValue={""}
            onChange={handleChange}
          />

          <Typography
            component="h2"
            variant="h5"
            align="center"
            sx={{ color: theme.palette.primary.main }}
          >
            {t("registration.login_data")}
          </Typography>
          <TextField
            required
            fullWidth
            id="email"
            label={t("registration.email")}
            name="email"
            autoComplete="off"
            inputProps={{
              autoComplete: "off",
              "data-lpignore": "true",
              "data-form-type": "other",
            }}
            value={newUser.email}
            onChange={handleChange}
            onBlur={(e) => validate_Email(e)}
            onFocus={() => {
              setHelperTextString("");
              setEmailErr(false);
            }}
            helperText={helperTextString}
            error={emailErr}
            type="email"
          />
          <Tooltip
            title={t("registration.passwordTooltip")}
            placement="top-start"
          >
            <FormControl
              fullWidth
              required
              error={pwdError}
              sx={{
                "& .Mui-error .MuiOutlinedInput-notchedOutline": {
                  borderColor: theme.palette.error.main,
                },
                "& .Mui-error .MuiInputLabel-outlined": {
                  color: theme.palette.error.main,
                },
              }}
            >
              <InputLabel
                htmlFor="outlined-adornment-password"
                sx={{
                  color: pwdError ? theme.palette.error.main : "inherit",
                  "&.Mui-focused": {
                    color: pwdError ? theme.palette.error.main : "inherit",
                  },
                }}
              >
                {t("registration.password")}
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                name="password"
                label={t("registration.password")}
                type={values.showPassword ? "text" : "password"}
                autoComplete="new-password"
                inputProps={{
                  autoComplete: "new-password",
                  "data-lpignore": "true",
                  "data-form-type": "other",
                }}
                value={newUser.password}
                onChange={handleChange}
                onBlur={(e) => validatePasswordValue(e.target.value)}
                error={pwdError}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      tabIndex={-1}
                    >
                      {values.showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {passwordErrors.map((errorText, index) => (
                <FormHelperText error key={`pwd-err-${index}`}>
                  {errorText}
                </FormHelperText>
              ))}
            </FormControl>
          </Tooltip>
          <FormControl fullWidth required={false} error={confirmPwdError}>
            <InputLabel
              htmlFor="outlined-adornment-confirm-password"
              sx={{
                color: confirmPwdError ? theme.palette.error.main : "inherit",
                "&.Mui-focused": {
                  color: confirmPwdError
                    ? theme.palette.error.main
                    : "inherit",
                },
              }}
            >
              {t("registration.confirm_password")}
            </InputLabel>
            <OutlinedInput
              id="outlined-adornment-confirm-password"
              name="confirmPassword"
              label={t("registration.confirm_password")}
              type={values.confirmPassword ? "text" : "password"}
              autoComplete="new-password"
              inputProps={{
                autoComplete: "new-password",
                "data-lpignore": "true",
                "data-form-type": "other",
              }}
              value={confirmPW}
              onChange={handleConfirmPasswordChange}
              onBlur={() =>
                validateConfirmPasswordValue(newUser.password, confirmPW)
              }
              error={confirmPwdError}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={handleClickShowConfirmePassword}
                    onMouseDown={handleMouseDownConfirmePassword}
                    edge="end"
                    tabIndex={-1}
                  >
                    {values.confirmPassword ? (
                      <VisibilityOff />
                    ) : (
                      <Visibility />
                    )}
                  </IconButton>
                </InputAdornment>
              }
            />
            {confirmPasswordErrors.map((errorText, index) => (
              <FormHelperText error key={`confirm-pwd-err-${index}`}>
                {errorText}
              </FormHelperText>
            ))}
          </FormControl>

          <Typography
            component="h2"
            variant="h5"
            align="center"
            sx={{ color: theme.palette.primary.main }}
          >
            {t("registration.other_information")}
          </Typography>

          {!newUser.individual && (
            <FormControlLabel
              control={
                <Checkbox
                  id="registered-company"
                  checked={companyRegisteredAsBusiness}
                  onChange={(e) =>
                    setCompanyRegisteredAsBusiness(e.target.checked)
                  }
                />
              }
              label={t("registration.registered_company_confirmation")}
              sx={{
                m: 0,
                color: theme.palette.primary.main,
                alignItems: "center",
              }}
            />
          )}
          <FormControlLabel
            control={
              <Checkbox
                required
                checked={agreeToPrivacyPolicy}
                id="privacy-policy"
                onChange={(e) => setAgreeToPrivacyPolicy(e.target.checked)}
              />
            }
            label={
              <Box component="span" sx={{ display: "inline" }}>
                {`${t("registration.privacy_policy_statement")} `}
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={handleClickOpen}
                  sx={{ verticalAlign: "baseline" }}
                >
                  {t("registration.privacy_policy_name")}
                </Link>
                <span> *</span>
              </Box>
            }
            sx={{
              m: 0,
              color: theme.palette.primary.main,
              alignItems: "center",
              "& .MuiFormControlLabel-label": {
                lineHeight: 1.5,
              },
            }}
          />

          <BootstrapDialog
            onClose={handleClose}
            aria-labelledby="customized-dialog-title"
            open={open}
          >
            <BootstrapDialogTitle
              id="customized-dialog-title"
              onClose={handleClose}
            >
              {t("registration.policy")}
            </BootstrapDialogTitle>
            <DialogContent dividers>{Policy}</DialogContent>
            <DialogActions>
              <Button
                autoFocus
                onClick={handleClose}
                sx={{ color: theme.palette.secondary.main }}
              >
                {t("registration.agree")}
              </Button>
            </DialogActions>
          </BootstrapDialog>

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!canSubmit}
            sx={{ background: theme.palette.secondary.main }}
            onClick={onSubmit}
          >
            {t("registration.submit_button_text")}
          </Button>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.main }}
          >
            {t("registration.requiredField")}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
}

export default Registration;
